import i18n from '../i18n'
import { bandOf } from './scoreBand'

/**
 * The archive's rules, mirrored from the Android app's `ArchiveScreen.kt`
 * (`filtered`, `summary`, `waveformBars`, `relativeDayLabel`) so the two
 * clients file the same take under the same chip and draw the same strip.
 */

const t = (key, options) => i18n.t(key, { ns: 'archive', ...options })

/**
 * The design's «بالای ۸۰»: a bar above the good band, not a fourth band. A
 * number the learner reads off the chip, so a number here rather than a
 * band name — the bands say how a take went, this says which to be proud of.
 */
export const ARCHIVE_TOP_SCORE = 80

/** The three filter chips, translated fresh on every call (never cached). */
export function filterOptions() {
  return [
    { key: 'all', label: t('filters.all') },
    { key: 'top', label: t('filters.top') },
    { key: 'weak', label: t('filters.weak') },
  ]
}

/**
 * The chip a row wears in place of a score, by `AttemptListItemDto.status`.
 *
 * `queued` is plan B's own status (V9-b/V9-w): the server has the take,
 * Azure has not scored it yet, and the requeue job or an admin will. That
 * is a wait, not a failure — so it takes the brand tint and «در صف», never
 * the red «تحلیل ناموفق» — and it is a wait the learner need do nothing
 * about, which is why the row still carries no retry. An unknown status
 * falls back to «در انتظار تحلیل»: a status this build has not heard of
 * must not render a blank chip.
 */
export function statusChip(status) {
  const map = {
    pending: { label: t('status.pending'), className: 'bg-surface-2 text-ink-2' },
    analyzing: { label: t('status.analyzing'), className: 'bg-brand-50 text-brand-700' },
    queued: { label: t('status.queued'), className: 'bg-brand-50 text-brand-700' },
    failed: { label: t('status.failed'), className: 'bg-bad-tint text-bad-ink' },
  }
  return map[status] ?? map.pending
}

/**
 * `weak` is every scored attempt below the good band, the mid band
 * included: the result screen's own advice for mid is to practise again.
 * An attempt with no score is in neither `top` nor `weak` — nothing was
 * judged. `items` are `AttemptListItemDto`s: `overall` is null unless scored.
 */
export function filterAttempts(items, filter) {
  switch (filter) {
    case 'top':
      return items.filter((item) => item.overall !== null && item.overall >= ARCHIVE_TOP_SCORE)
    case 'weak':
      return items.filter((item) => item.overall !== null && bandOf(item.overall) !== 'good')
    default:
      return items
  }
}

/** The three tiles above the list, from the scored rows only; null (never 0) when nothing was scored. */
export function summarize(items) {
  const scores = items.map((item) => item.overall).filter((score) => score !== null && score !== undefined)
  return {
    bestScore: scores.length === 0 ? null : Math.max(...scores),
    averageScore: scores.length === 0 ? null : Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length),
    topCount: scores.filter((score) => score >= ARCHIVE_TOP_SCORE).length,
  }
}

/**
 * Deterministic bar heights, the Android app's `waveformBars` verbatim: the
 * same clip must draw the same strip on every render, and a jittering strip
 * reads as a bug. Seeded from the attempt id, not measured — the server
 * keeps no amplitude data — so the strip moves with the clip rather than
 * pretending to be the clip.
 */
export function waveformBars(count, seed, scale) {
  return Array.from({ length: count }, (_, i) => {
    const a = Math.abs(Math.sin((i + seed) * 0.71))
    const b = Math.abs(Math.cos((i + seed) * 0.29))
    return Math.round(4 + (a * 0.68 + b * 0.32) * scale)
  })
}

/** A stable small integer from an id, for `waveformBars`' seed. */
export function seedOf(id) {
  let hash = 0
  for (const char of String(id)) hash = (hash * 31 + char.charCodeAt(0)) % 100_000
  return hash
}

/**
 * «امروز» / «دیروز» / «N روز پیش», by calendar day of the device, from an
 * ISO instant. Mirrors `relativeDayLabel` in the Android nav host; `faDigits`
 * is applied by the caller so the rule stays free of rendering.
 */
export function daysAgo(isoInstant, now = new Date()) {
  const then = new Date(isoInstant)
  if (Number.isNaN(then.getTime())) return null
  const startOf = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  return Math.max(0, Math.round((startOf(now) - startOf(then)) / 86_400_000))
}
